import React, { useState } from 'react';

import { IconButton, Typography } from '@mui/material';
import { AlineTooltip, UploadFileVersionPicker } from 'src/components';
import { AddIcon } from 'src/components/icons/AddIcon';
import type { Apollo } from 'src/gql/generated';
import { FileStackDomContainer } from 'src/helpers';
import { useGetDocumentWithWorkflowState, useIsAuthorizedToEditStorable } from 'src/hooks';
import { alineLinkButtonStyles, appColors } from 'src/theme';
import { getWorkflowWasSentForSig } from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles<StyleProps>()((theme, { disabled }) => ({
  uploadText: {
    ...alineLinkButtonStyles,
    color: disabled ? theme.palette.text.disabled : appColors.link,
    cursor: !disabled ? 'pointer' : undefined,
    marginBottom: theme.spacing(2),
    width: 'fit-content',
  },
}));

interface StyleProps {
  disabled?: boolean;
}

interface UploadVersionProps {
  documentId: UUID;
  afterUpload?: (fileVersion: Pick<Apollo.FileVersion, 'id' | '__typename'>) => Promise<void>;
  icon?: React.ReactNode;
  iconWrapperClass?: string;
  variant?: 'button' | 'text';
}

export function UploadVersion({
  afterUpload,
  documentId,
  icon,
  iconWrapperClass,
  variant = 'button',
}: UploadVersionProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const { canEdit } = useIsAuthorizedToEditStorable(documentId);
  const { document } = useGetDocumentWithWorkflowState(documentId);
  const wasSentForSigning = getWorkflowWasSentForSig(document?.workflow ?? null);
  const disabledReason = wasSentForSigning
    ? 'Cannot upload version after sending for signature'
    : document?.isSigned
      ? 'Cannot upload version after signing'
      : !canEdit
        ? 'Not authorized to upload version'
        : '';
  const { classes } = useStyles({ disabled: Boolean(disabledReason) });

  switch (variant) {
    case 'button': {
      return (
        <>
          <AlineTooltip arrow placement="top" title={disabledReason || 'Upload version'}>
            <div>
              <IconButton
                className={iconWrapperClass}
                disabled={Boolean(disabledReason)}
                size="small"
                onClick={(): void => setOpen(!open)}
              >
                {icon || <AddIcon />}
              </IconButton>
            </div>
          </AlineTooltip>
          <UploadFileVersionPicker
            domContainer={FileStackDomContainer.FileVersion}
            open={open}
            pickerOpts={{
              afterUpload,
              documentId,
              onClose: () => setOpen(false),
            }}
          />
        </>
      );
    }
    case 'text': {
      return (
        <>
          <AlineTooltip arrow placement="top" title={disabledReason}>
            <Typography
              className={classes.uploadText}
              onClick={() => {
                if (!disabledReason) setOpen(!open);
              }}
            >
              Upload new version
            </Typography>
          </AlineTooltip>
          <UploadFileVersionPicker
            domContainer={FileStackDomContainer.FileVersion}
            open={open}
            pickerOpts={{
              afterUpload,
              documentId,
              onClose: () => setOpen(false),
            }}
          />
        </>
      );
    }
  }
}
